// The display language of the Competitor Benchmark surface, in one place.
//
// Ported from Meteoric's `src/lib/benchmark-view.ts`. The freshness THRESHOLDS
// are deliberately not ported: the producer already decides `freshness_status`,
// and re-deriving it here would give two definitions of "stale" that disagree
// the first time one of them changes.

import type {
  BenchmarkPayload,
  FreshnessStatus,
  Marketplace,
  Metric,
  MetricBlock,
} from "./api";

const MARKETPLACE_LABELS: Record<string, string> = {
  UK: "United Kingdom",
  US: "United States",
  DE: "Germany",
  FR: "France",
  ES: "Spain",
  IT: "Italy",
  NL: "Netherlands",
  BE: "Belgium",
  PL: "Poland",
  IE: "Ireland",
  SE: "Sweden",
  AE: "United Arab Emirates",
  SA: "Saudi Arabia",
};

export function marketplaceLabel(marketplace: string): string {
  const code = marketplace.trim().toUpperCase();
  return MARKETPLACE_LABELS[code] ?? code;
}

// The payload carries no currency, so the symbol is resolved by marketplace.
const CURRENCY: Record<string, string> = {
  UK: "£", US: "$", DE: "€", FR: "€", ES: "€", IT: "€",
  NL: "€", BE: "€", IE: "€", PL: "zł", SE: "kr", AE: "AED", SA: "SAR",
};

export function currencySymbol(marketplace: string): string {
  return CURRENCY[marketplace.toUpperCase()] ?? "";
}

/**
 * A metric value with its unit, always.
 *
 * A BSR is a rank and takes a `#`; a price takes its marketplace's symbol. The
 * two are never interchangeable, and a bare `12849` next to a bare `24.99`
 * invites exactly that confusion. A missing value renders "n/a", never 0 —
 * a rank of 0 would be the best possible position.
 */
export function formatMetricValue(
  metric: Metric,
  value: number | null,
  marketplace: string,
): string {
  if (value === null || Number.isNaN(value)) return "n/a";
  if (metric === "bsr") {
    return `#${new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 }).format(value)}`;
  }
  const amount = new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
  const symbol = currencySymbol(marketplace);
  return symbol.length > 1 ? `${symbol} ${amount}` : `${symbol}${amount}`;
}

/**
 * The two ends of the band, named for what they MEAN rather than their maths.
 *
 * For price, `max` is the most expensive competitor: "High". For BSR a higher
 * number is a WORSE rank, so the same `max` is "Worst". Labelling both "Max"
 * reads as a compliment on the BSR chart and is the single easiest way to
 * misread this surface.
 */
export function extremeLabels(metric: Metric): { max: string; min: string } {
  return metric === "price" ? { max: "High", min: "Low" } : { max: "Worst", min: "Best" };
}

export function metricLabel(metric: Metric): string {
  return metric === "price" ? "Price" : "Best Sellers Rank";
}

export type Tone = "critical" | "watch" | "ok" | "neutral";

/**
 * A freshness status as a tone, not a colour.
 *
 * Meteoric painted these from `--color-ks-pass/warn/fail`. This portal's brand
 * layer has no green and no amber, and one rule about red: danger and nothing
 * else. So the status is carried by its written sentence, and only a broken
 * collection is destructive.
 */
export function statusTone(status: FreshnessStatus): Tone {
  if (status === "critical") return "critical";
  if (status === "stale") return "watch";
  if (status === "fresh") return "ok";
  return "neutral";
}

export function badgeVariant(tone: Tone): "default" | "secondary" | "destructive" | "outline" {
  if (tone === "critical") return "destructive";
  if (tone === "ok") return "secondary";
  return "outline";
}

export function ageDays(lastCollectedAt: string | null, today: Date): number | null {
  if (!lastCollectedAt) return null;
  const collected = Date.parse(`${lastCollectedAt}T00:00:00Z`);
  if (Number.isNaN(collected)) return null;
  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return Math.max(0, Math.round((todayUtc - collected) / 86_400_000));
}

export type FreshnessFlag = { tone: Tone; message: string };

/**
 * The producer's status turned into a sentence a reader can act on.
 *
 * "critical" says the collection may be BROKEN rather than that the data is
 * merely old, because those call for different actions: one is a wait, the
 * other is a fix.
 */
export function freshnessFlag(
  status: FreshnessStatus,
  lastCollectedAt: string | null,
  today: Date,
): FreshnessFlag {
  const age = ageDays(lastCollectedAt, today);
  const ageText = age === null ? "of unknown age" : age === 1 ? "1 day old" : `${age} days old`;
  const lastText = lastCollectedAt ? ` (last collected ${lastCollectedAt})` : "";
  switch (status) {
    case "fresh":
      return { tone: "ok", message: `Competition data is up to date${lastText}` };
    case "stale":
      return { tone: "watch", message: `Competition data is ${ageText}${lastText}` };
    case "critical":
      return {
        tone: "critical",
        message: `Competition data is ${ageText}. Collection may be broken${lastText}`,
      };
    default:
      return { tone: "neutral", message: "Awaiting the first collection for this selection" };
  }
}

/**
 * The worst status across a payload's variants, for the tab-level banner.
 *
 * Worst-of, never newest: one dead ASIN must not hide behind a fresh sibling.
 * A tab that reports its healthiest variant is a tab that never reports a
 * problem.
 */
const STATUS_RANK: Record<FreshnessStatus, number> = {
  fresh: 0,
  no_data: 1,
  stale: 2,
  critical: 3,
};

export function worstFreshness(
  payload: BenchmarkPayload,
  metric: Metric,
  today: Date,
): FreshnessFlag {
  let worst: { status: FreshnessStatus; last: string | null } | null = null;
  for (const entry of Object.values(payload.variants)) {
    const block: MetricBlock | undefined = entry[metric];
    if (!block) continue;
    if (!worst || STATUS_RANK[block.freshness_status] > STATUS_RANK[worst.status]) {
      worst = { status: block.freshness_status, last: block.last_collected_at };
    }
  }
  if (!worst) return { tone: "neutral", message: "Awaiting the first collection" };
  return freshnessFlag(worst.status, worst.last, today);
}

/**
 * A variant's display name, falling back to its raw id.
 *
 * The id is ugly, but a nameless variant still has to be SELECTABLE — a blank
 * option is one a person cannot pick, so the whole variant becomes unreachable.
 */
export function variantRowLabel(
  product: string | null | undefined,
  variant: string | null | undefined,
  id: string,
): string {
  const parts = [product, variant].filter((p): p is string => Boolean(p && p.trim()));
  if (parts.length === 0) return id;
  // Dedup: a variant named the same as its product reads as a stutter.
  const unique = parts.filter((part, index) => parts.indexOf(part) === index);
  return unique.join(" · ");
}

export type VariantOption = { id: string; label: string };

export function variantOptions(payload: BenchmarkPayload): VariantOption[] {
  return Object.entries(payload.variants)
    .map(([id, entry]) => ({ id, label: variantRowLabel(entry.product, entry.variant, id) }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The variant to show when none is chosen: the first that actually HAS
 * observations for this metric.
 *
 * Defaulting to the alphabetically-first variant lands a reader on an empty
 * chart whenever that one happens to be uncollected, and an empty chart on
 * arrival reads as a broken module rather than as one unpopulated variant.
 */
export function defaultVariant(
  payload: BenchmarkPayload,
  metric: Metric,
  requested: string | null,
): string | null {
  if (requested && payload.variants[requested]) return requested;
  const options = variantOptions(payload);
  const withData = options.find((o) => (payload.variants[o.id]?.[metric]?.series ?? []).length > 0);
  return withData?.id ?? options[0]?.id ?? null;
}

export function defaultMarketplace(requested: string | null): Marketplace {
  const code = (requested ?? "").toUpperCase();
  return (MARKETPLACES_SET.has(code) ? code : "UK") as Marketplace;
}

const MARKETPLACES_SET = new Set([
  "UK", "DE", "FR", "ES", "IT", "NL", "BE", "PL", "IE", "SE", "AE", "SA", "US",
]);
